import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  ExternalLink,
  Loader2,
  RotateCcw,
  Search,
  ShieldCheck,
} from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Input,
  Select,
  Stepper,
  useToast,
  cn,
} from '@solara/ui-shared';
import {
  PeppolProviderError,
  type Participant,
  type ParticipantScheme,
  type PeppolParticipantId,
} from '@solara/peppol-provider';
import { usePeppolConnectivity } from '@solara/peppol-tickstar/react';
import {
  usePeppolActivate,
  usePeppolLookup,
  usePeppolPublish,
  usePeppolRegister,
} from '@solara/api-client/react';

type StepKey = 'lookup' | 'register' | 'activate' | 'publish' | 'done';

const STEPS: { key: StepKey; label: string }[] = [
  { key: 'lookup',   label: 'Lookup' },
  { key: 'register', label: 'Register' },
  { key: 'activate', label: 'Activate (SML)' },
  { key: 'publish',  label: 'Publish (Directory)' },
  { key: 'done',     label: 'Done' },
];

const SCHEMES: { value: ParticipantScheme; label: string; sample: string }[] = [
  { value: '0230', label: 'UAE · MY:EIF analogue (pilot)', sample: 'UAE-TRN-100000000000007' },
  { value: '0088', label: 'GLN (international)', sample: '7300010000007' },
  { value: '0151', label: 'AU · ABN (test)', sample: '51824753556' },
  { value: '0195', label: 'SG · UEN (KYC required)', sample: 'SGUEN20251234A' },
];

const DOCUMENT_PROFILES = [
  { id: 'peppol-bis-billing-3.0-invoice', label: 'PEPPOL BIS Billing 3.0 — Invoice' },
  { id: 'peppol-bis-billing-3.0-credit-note', label: 'PEPPOL BIS Billing 3.0 — Credit Note' },
];

/**
 * Customer portal · Peppol participant registration flow.
 *
 * Walks a tenant through SMP registration → SML activation → Peppol Directory
 * publish, talking to the TickStar SMP Manager API via the
 * `PeppolGatewayProvider`. In demo mode this is the in-memory mock; in
 * production it's the live TickStar SMP.
 */
export default function PeppolRegistrationPage() {
  const { t } = useTranslation('tickstar');
  const { t: tP1 } = useTranslation('p1');
  const { locale } = useI18n();
  const { toast } = useToast();
  const connectivity = usePeppolConnectivity();
  const lookup = usePeppolLookup();
  const register = usePeppolRegister();
  const activate = usePeppolActivate();
  const publish = usePeppolPublish();
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }),
    [locale],
  );

  const [scheme, setScheme] = useState<ParticipantScheme>('0230');
  const [identifier, setIdentifier] = useState('UAE-TRN-100000000000007');
  const [legalName, setLegalName] = useState('Solara Demo Tenant LLC');
  const [country, setCountry] = useState('AE');
  const [profiles, setProfiles] = useState<string[]>([
    'peppol-bis-billing-3.0-invoice',
    'peppol-bis-billing-3.0-credit-note',
  ]);

  const participantId = useMemo<PeppolParticipantId>(
    () => `iso6523-actorid-upis::${scheme}:${identifier}` as PeppolParticipantId,
    [scheme, identifier],
  );

  const [step, setStep] = useState<StepKey>('lookup');
  const [lookupResult, setLookupResult] = useState<
    'unknown' | 'searching' | 'free' | 'registered' | 'error'
  >('unknown');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [participant, setParticipant] = useState<Participant | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  // Re-run the lookup whenever the identifier changes — gives the user
  // immediate feedback if they pick an already-registered ID.
  useEffect(() => {
    setLookupResult('unknown');
    setErrorMessage(undefined);
  }, [participantId]);

  const handleLookup = async () => {
    setLookupResult('searching');
    setErrorMessage(undefined);
    try {
      const res = await lookup.mutateAsync(participantId);
      if (res.participant) {
        setLookupResult('registered');
        setParticipant(res.participant as unknown as Participant);
      } else {
        setLookupResult('free');
      }
    } catch (e) {
      setLookupResult('error');
      setErrorMessage(e instanceof Error ? e.message : String(e));
    }
  };

  const handleRegister = async () => {
    setBusy(true);
    setErrorMessage(undefined);
    try {
      const result = await register.mutateAsync({
        scheme,
        identifier,
        legalName,
        country,
        documentProfiles: profiles,
        activate: false,
        publishInDirectory: false,
      });
      setParticipant(result.participant as unknown as Participant);
      if (result.requiresKyc) {
        toast({
          title: t('errors.KYC_REQUIRED'),
          description: 'Submit the KYC document to finalise registration.',
          tone: 'warning',
        });
        setStep('register');
      } else {
        setStep('activate');
        toast({ title: 'Registered', description: participantId, tone: 'success' });
      }
    } catch (e) {
      handleProviderError(e);
    } finally {
      setBusy(false);
    }
  };

  const handleActivate = async () => {
    setBusy(true);
    setErrorMessage(undefined);
    try {
      await activate.mutateAsync(participantId);
      const refreshed = await lookup.mutateAsync(participantId);
      if (refreshed.participant) setParticipant(refreshed.participant as unknown as Participant);
      setStep('publish');
      toast({ title: 'Activated in SML', tone: 'success' });
    } catch (e) {
      handleProviderError(e);
    } finally {
      setBusy(false);
    }
  };

  const handlePublish = async () => {
    setBusy(true);
    setErrorMessage(undefined);
    try {
      await publish.mutateAsync(participantId);
      const refreshed = await lookup.mutateAsync(participantId);
      if (refreshed.participant) setParticipant(refreshed.participant as unknown as Participant);
      setStep('done');
      toast({ title: 'Published in Peppol Directory', tone: 'success' });
    } catch (e) {
      handleProviderError(e);
    } finally {
      setBusy(false);
    }
  };

  const handleReset = () => {
    setStep('lookup');
    setLookupResult('unknown');
    setParticipant(undefined);
    setErrorMessage(undefined);
  };

  function handleProviderError(e: unknown) {
    if (e instanceof PeppolProviderError) {
      const key = `errors.${e.code}`;
      setErrorMessage(t(key, { defaultValue: e.message }));
      toast({ title: t(key, { defaultValue: e.message }), tone: 'danger' });
    } else {
      setErrorMessage(e instanceof Error ? e.message : String(e));
      toast({ title: 'Unknown error', tone: 'danger' });
    }
  }

  const stepIndex = STEPS.findIndex((s) => s.key === step);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{tP1('peppol.title')}</h1>
        <p className="text-sm text-fg-muted">{tP1('peppol.subtitle')}</p>
        {connectivity.mode === 'mock' ? (
          <Badge tone="warning" className="mt-2">
            <AlertTriangle className="h-3 w-3" />
            {t('admin.overview.modeMock')} · {connectivity.label}
          </Badge>
        ) : (
          <Badge tone="success" className="mt-2">
            <ShieldCheck className="h-3 w-3" />
            {connectivity.label}
          </Badge>
        )}
      </header>

      <Card>
        <CardBody>
          <Stepper
            steps={STEPS.map((s) => ({ key: s.key, label: s.label }))}
            current={stepIndex < 0 ? 0 : stepIndex}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{tP1('peppol.participantDetails')}</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <label className="text-xs font-medium text-fg-muted">{tP1('peppol.scheme')}</label>
            <Select
              value={scheme}
              onChange={(e) => setScheme(e.target.value as ParticipantScheme)}
              options={SCHEMES.map((s) => ({ value: s.value, label: `${s.value} · ${s.label}` }))}
            />
            <p className="text-[11px] text-fg-subtle">
              UAE Peppol scheme TBC by FTA — pilot uses 0230 (MY:EIF) analogue.
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-fg-muted">Identifier</label>
            <Input
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={SCHEMES.find((s) => s.value === scheme)?.sample}
            />
            <p className="font-mono text-[11px] text-fg-subtle break-all">{participantId}</p>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-fg-muted">Legal name</label>
            <Input value={legalName} onChange={(e) => setLegalName(e.target.value)} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-fg-muted">Country (ISO-3166)</label>
            <Input
              value={country}
              onChange={(e) => setCountry(e.target.value.toUpperCase().slice(0, 2))}
              maxLength={2}
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <label className="text-xs font-medium text-fg-muted">Document profiles</label>
            <div className="flex flex-wrap gap-2">
              {DOCUMENT_PROFILES.map((p) => {
                const selected = profiles.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() =>
                      setProfiles((prev) =>
                        prev.includes(p.id) ? prev.filter((x) => x !== p.id) : [...prev, p.id],
                      )
                    }
                    className={cn(
                      'rounded-md border px-3 py-1.5 text-xs',
                      selected
                        ? 'border-brand-500 bg-brand-50 text-brand-700'
                        : 'border-slate-300 text-slate-700 hover:bg-slate-50',
                    )}
                  >
                    {selected ? '✓ ' : ''}
                    {p.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-fg-subtle">{t('tools.smpDocsHint')}</p>
          </div>
        </CardBody>
      </Card>

      {/* STEP CONTROL ------------------------------------------------ */}
      {step === 'lookup' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-4 w-4" /> Check participant
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <p className="text-sm text-fg-muted">
              Before registering, check whether this Peppol ID is already on the network.
            </p>
            <div className="flex gap-2">
              <Button onClick={handleLookup} disabled={lookupResult === 'searching'}>
                {lookupResult === 'searching' ? (
                  <Loader2 className="me-1 h-4 w-4 animate-spin" />
                ) : null}
                {t('tools.lookupCta')}
              </Button>
              {lookupResult === 'free' && (
                <Button variant="primary" onClick={handleRegister} disabled={busy}>
                  Register
                  <ChevronRight className="ms-1 h-4 w-4" />
                </Button>
              )}
            </div>
            <LookupHint result={lookupResult} participantId={participantId} t={t} />
            {errorMessage ? (
              <p className="text-sm text-rose-700">
                <AlertTriangle className="me-1 inline h-3.5 w-3.5" /> {errorMessage}
              </p>
            ) : null}
          </CardBody>
        </Card>
      )}

      {step === 'register' && participant?.status === 'KYC_REQUIRED' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" /> KYC required
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <p className="text-sm">
              This scheme requires KYC. In production you'd POST a business-registration PDF to
              <code className="mx-1 rounded bg-slate-100 px-1 text-xs">
                /2.1/smp/kyc/pdf/sign/{participantId}
              </code>
              and wait for automatic SML activation.
            </p>
            <Button
              variant="primary"
              onClick={async () => {
                // Mock: treat KYC as approved and proceed directly to activation.
                setStep('activate');
              }}
            >
              Simulate KYC approval (demo)
              <ChevronRight className="ms-1 h-4 w-4" />
            </Button>
          </CardBody>
        </Card>
      )}

      {step === 'activate' && (
        <Card>
          <CardHeader>
            <CardTitle>Activate in SML</CardTitle>
          </CardHeader>
          <CardBody className="space-y-2">
            <p className="text-sm text-fg-muted">
              Publishes the participant's SMP record to the central Service Metadata Locator —
              other access points can now route documents to this participant.
            </p>
            <Button onClick={handleActivate} disabled={busy}>
              {busy ? <Loader2 className="me-1 h-4 w-4 animate-spin" /> : null}
              Activate in SML
            </Button>
          </CardBody>
        </Card>
      )}

      {step === 'publish' && (
        <Card>
          <CardHeader>
            <CardTitle>Publish in Peppol Directory</CardTitle>
          </CardHeader>
          <CardBody className="space-y-2">
            <p className="text-sm text-fg-muted">
              Makes the participant discoverable via the Peppol Directory yellow-pages
              (
              <a
                href="https://directory.peppol.eu/public"
                target="_blank"
                rel="noreferrer"
                className="text-brand-700 underline-offset-2 hover:underline"
              >
                directory.peppol.eu <ExternalLink className="ms-1 inline h-3 w-3" />
              </a>
              ).
            </p>
            <Button onClick={handlePublish} disabled={busy}>
              {busy ? <Loader2 className="me-1 h-4 w-4 animate-spin" /> : null}
              Publish
            </Button>
          </CardBody>
        </Card>
      )}

      {step === 'done' && participant && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" /> Live on Peppol
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-3 text-sm">
            <div className="space-y-1 font-mono text-xs">
              <div>
                <span className="text-slate-500">participantId:</span> {participant.participantId}
              </div>
              <div>
                <span className="text-slate-500">status:</span>{' '}
                {t(`participant.status.${participant.status}`, { defaultValue: participant.status })}
              </div>
              <div>
                <span className="text-slate-500">smlActive:</span>{' '}
                {participant.smlActive ? '✓' : '—'}
              </div>
              <div>
                <span className="text-slate-500">publishedInDirectory:</span>{' '}
                {participant.publishedInDirectory ? '✓' : '—'}
              </div>
              <div>
                <span className="text-slate-500">updatedAt:</span>{' '}
                {dateFmt.format(new Date(participant.updatedAt))}
              </div>
            </div>
            <Button variant="ghost" onClick={handleReset}>
              <RotateCcw className="me-1 h-4 w-4" /> Register another participant
            </Button>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function LookupHint({
  result,
  participantId,
  t,
}: {
  result: 'unknown' | 'searching' | 'free' | 'registered' | 'error';
  participantId: PeppolParticipantId;
  t: (k: string, vars?: Record<string, unknown>) => string;
}) {
  if (result === 'unknown')
    return (
      <p className="text-xs text-slate-500 font-mono break-all">
        Will look up <span className="text-slate-700">{participantId}</span>.
      </p>
    );
  if (result === 'searching')
    return (
      <p className="text-xs text-slate-500 inline-flex items-center gap-1">
        <Loader2 className="h-3 w-3 animate-spin" /> Looking up via TickStar SMP…
      </p>
    );
  if (result === 'free')
    return (
      <p className="text-xs text-emerald-700 inline-flex items-center gap-1">
        <CheckCircle2 className="h-3.5 w-3.5" /> {participantId} is available.
      </p>
    );
  if (result === 'registered')
    return (
      <p className="text-xs text-rose-700 inline-flex items-center gap-1">
        <AlertTriangle className="h-3.5 w-3.5" /> Already registered — pick a different
        identifier.
      </p>
    );
  return null;
}
